import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header } from "../components/app/Header";
import { FollowButton } from "../components/app/FollowButton";
import { CommentsSection } from "../components/app/CommentsSection";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";
import {
  ArrowUpRight,
  ArrowDownRight,
  MinusSquare,
  Heart,
  Loader2,
  Twitter,
  Link2,
  ArrowLeft,
  AlertTriangle,
  Flame,
} from "lucide-react";

const DirectionMeta = (d) => {
  if (d === "BULLISH")
    return { color: "text-bull", glow: "glow-bull", Icon: ArrowUpRight, key: "BULLISH" };
  if (d === "BEARISH")
    return { color: "text-bear", glow: "glow-bear", Icon: ArrowDownRight, key: "BEARISH" };
  return { color: "text-neutral-400", glow: "", Icon: MinusSquare, key: "NEUTRAL" };
};

const confidenceColor = (c) => {
  if (c >= 70) return { bar: "bg-bull", text: "text-bull" };
  if (c >= 55) return { bar: "bg-warn", text: "text-warn" };
  return { bar: "bg-bear", text: "text-bear" };
};

export default function CommunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/community/${id}`);
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Analysis not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const toggleLike = async () => {
    if (!user) {
      toast(t("community_login_to_like"));
      navigate("/login");
      return;
    }
    const prev = data;
    setData({
      ...data,
      liked_by_me: !data.liked_by_me,
      share_likes: data.share_likes + (data.liked_by_me ? -1 : 1),
    });
    try {
      await api.post(`/community/${id}/like`);
    } catch {
      setData(prev);
      toast.error("Failed to update like");
    }
  };

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const shareTwitter = () => {
    const text = `${data.symbol || "Chart"} · ${t(`direction_${data.direction}`)} ${data.confidence}% — analyzed with CANDLE.AI`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("community_link_copied"));
    } catch {
      toast.error("Copy failed");
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-bull" />
      </div>
    );

  if (err)
    return (
      <div className="min-h-screen">
        <Header />
        <div className="mx-auto max-w-2xl p-10 text-center">
          <p className="text-neutral-400">{err}</p>
          <button
            onClick={() => navigate("/community")}
            className="mt-4 inline-block font-mono text-sm text-bull"
          >
            {t("community_back")}
          </button>
        </div>
      </div>
    );

  const meta = DirectionMeta(data.direction);
  const cc = confidenceColor(data.confidence);

  return (
    <div className="min-h-screen" data-testid="community-detail-page">
      <Header />
      <main className="mx-auto max-w-6xl px-6 md:px-8 py-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/community")}
            className="text-neutral-400 hover:text-white hover:bg-white/5"
            data-testid="back-to-community"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
            {t("community_back")}
          </Button>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              className="rounded-none border-white/15 bg-transparent hover:bg-bull/5 hover:border-bull/40 hover:text-bull h-9"
              onClick={shareTwitter}
              data-testid="share-twitter-btn"
            >
              <Twitter className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
              {t("community_share_twitter")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-none border-white/15 bg-transparent hover:bg-bull/5 hover:border-bull/40 hover:text-bull h-9"
              onClick={copyLink}
              data-testid="share-link-btn"
            >
              <Link2 className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
              {t("community_share_discord")}
            </Button>
            <button
              onClick={toggleLike}
              className={`inline-flex items-center gap-1.5 px-3 h-9 border font-mono text-xs transition-colors ${
                data.liked_by_me
                  ? "border-bear/40 text-bear bg-bear/[0.06]"
                  : "border-white/15 text-neutral-300 hover:border-bear/40 hover:text-bear"
              }`}
              data-testid="detail-like-btn"
              aria-pressed={data.liked_by_me}
            >
              <Heart
                className="h-3.5 w-3.5"
                strokeWidth={2}
                fill={data.liked_by_me ? "currentColor" : "none"}
              />
              <span data-testid="detail-like-count">{data.share_likes}</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Chart */}
          <div className="lg:col-span-7 surface-card overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <span className="label-mono">
                CHART // {data.symbol || "UNTAGGED"} {data.timeframe ? `· ${data.timeframe}` : ""}
              </span>
              <div className="flex items-center gap-2">
                {data.is_trending && (
                  <span
                    className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.18em] px-1.5 py-0.5 border border-warn/40 text-warn bg-warn/[0.06]"
                    data-testid="detail-trending-badge"
                  >
                    <Flame className="h-2.5 w-2.5" strokeWidth={2.5} />
                    {t("trending_badge")}
                  </span>
                )}
                <span className="font-mono text-[11px] text-neutral-500">
                  {t("community_by")} {data.author_name}
                </span>
                {data.author_user_id && (
                  <FollowButton
                    authorId={data.author_user_id}
                    initialFollowing={data.followed_by_me}
                    compact
                  />
                )}
              </div>
            </div>
            <div className="bg-black aspect-[16/10] flex items-center justify-center">
              {data.thumbnail ? (
                <img
                  src={data.thumbnail}
                  alt="chart"
                  className="max-w-full max-h-full object-contain"
                  data-testid="community-thumbnail"
                />
              ) : (
                <span className="label-mono">NO_PREVIEW</span>
              )}
            </div>
          </div>

          {/* Prediction + Levels */}
          <div className="lg:col-span-5 space-y-4">
            <div className={`surface-card p-6 ${meta.glow}`} data-testid="community-prediction-card">
              <div className="flex items-center justify-between">
                <span className="label-mono">{t("prediction_label")}</span>
                <span className="font-mono text-[10px] tracking-[0.18em] px-2 py-0.5 border border-white/15 text-neutral-300">
                  {data.market_type}
                </span>
              </div>
              <div className="mt-4 flex items-baseline gap-3">
                <meta.Icon className={`h-10 w-10 ${meta.color}`} strokeWidth={1.5} />
                <span className={`font-mono text-5xl font-medium ${meta.color}`}>
                  {t(`direction_${meta.key}`)}
                </span>
              </div>
              <div className="mt-4 font-mono text-sm flex items-center justify-between">
                <span className="text-neutral-500">{t("confidence_label")}</span>
                <span className={cc.text}>{data.confidence}%</span>
              </div>
              <div className="mt-2 h-2 bg-white/5 overflow-hidden">
                <div className={`h-full ${cc.bar}`} style={{ width: `${data.confidence}%` }} />
              </div>
              {data.analysis_summary && (
                <p className="mt-5 text-sm text-neutral-300 leading-relaxed">
                  {data.analysis_summary}
                </p>
              )}
            </div>

            <div className="surface-card p-6" data-testid="community-levels-card">
              <div className="flex items-center justify-between mb-3">
                <span className="label-mono">{t("levels_label")}</span>
                {data.risk_reward && (
                  <div className="text-right">
                    <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-neutral-500">
                      {t("rr_label")}
                    </div>
                    <div className={`font-mono text-2xl ${meta.color}`}>{data.risk_reward}</div>
                  </div>
                )}
              </div>
              <div className="divide-y divide-white/5">
                {[
                  { label: t("entry"), value: data.entry, color: "text-white" },
                  { label: t("tp1"), value: data.tp1, color: "text-bull" },
                  {
                    label: t("tp2"),
                    value: data.tp2,
                    color: "",
                    style: { color: "#FF8C00" },
                  },
                  { label: t("stop_loss"), value: data.stop_loss, color: "text-bear" },
                ].map((lv, i) => (
                  <div key={i} className="py-3 flex items-baseline justify-between gap-4">
                    <span className="font-mono text-xs uppercase tracking-wider text-neutral-400">
                      {lv.label}
                    </span>
                    <span className={`font-mono text-lg ${lv.color}`} style={lv.style || {}}>
                      {lv.value || "—"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Patterns */}
        {(data.detected_patterns || []).length > 0 && (
          <div className="surface-card p-6" data-testid="community-patterns">
            <span className="label-mono">{t("patterns_label")}</span>
            <div className="mt-3 flex flex-wrap gap-2">
              {data.detected_patterns.map((p, i) => (
                <span
                  key={i}
                  className="inline-flex items-center px-3 py-1.5 border border-bull/20 bg-bull/[0.04] font-mono text-xs text-bull"
                >
                  {p}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Comments */}
        <CommentsSection analysisId={data.analysis_id} ownerUserId={data.author_user_id} />

        {/* Disclaimer */}
        <div
          className="border border-white/10 bg-white/[0.02] p-4 flex items-start gap-3"
          data-testid="community-disclaimer"
        >
          <AlertTriangle className="h-4 w-4 text-neutral-500 shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-[11px] font-mono text-neutral-500 leading-relaxed">
            {t("disclaimer")}
          </p>
        </div>
      </main>
    </div>
  );
}
